import C from "@/components/sidebar/StandardSidebar.module.css";

import type { Action } from "@/components/Action.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import type { SortFieldInfo, SortState } from "@/components/data/getSortedItems.ts";
import { WSearchField } from "@/components/forms/WSearchField.tsx";
import { NoResults } from "@/components/sidebar/EmptyItem.tsx";
import { ListErrorBoundary, QueryErrorBlocks } from "@/components/sidebar/ListErrorBoundary.tsx";
import { SortMenu } from "@/components/sidebar/SortMenu.tsx";
import { ListBlock, SidebarHeader, SkeletonBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { useStoredSort } from "@/components/sidebar/useStoredSort.ts";
import type { QStatus } from "@/utils/query/mergeQueries.ts";
import { Trans } from "@lingui/react/macro";
import type { ReactElement, ReactNode } from "react";
import { Suspense, useState } from "react";

export interface SidebarListState extends SortState {
    queryString: string;
}

export type SidebarListId = "access" | "network" | "compute" | "storage" | "billing" | "services" | "metal" | "disk";

export interface SidebarListProps {
    data: (props: SidebarListState) => ReactNode;
    title: string;
    sortFields: SortFieldInfo[];
    actionButton: {
        title: string;
        titleShort?: ReactNode;
        action: Action;
    };
    listId: SidebarListId;
}

export function SidebarList({
    title,
    sortFields,
    data,
    listId,
    actionButton: { action, title: titleLong, titleShort = <Trans>New</Trans> },
}: SidebarListProps) {
    const [queryString, setQueryString] = useState("");
    const { sortId, setSortId, sortAsc, setSortAsc } = useStoredSort(listId, sortFields);

    return (
        <>
            <SidebarHeader
                title={title}
                searchField={<WSearchField value={queryString} onChange={setQueryString} />}
                sortMenu={
                    <SortMenu
                        sortFields={sortFields}
                        sortId={sortId}
                        onSortId={setSortId}
                        sortAsc={sortAsc}
                        onSortAsc={setSortAsc}
                    />
                }
            >
                <WButton label={titleShort} color="sidebar" size="bar" action={action} />
            </SidebarHeader>

            <ListBlock>
                <ListErrorBoundary>
                    <Suspense fallback={<SkeletonBlock />}>{data({ sortId, sortAsc, queryString })}</Suspense>
                </ListErrorBoundary>

                <div className="p-2">
                    <WButton
                        className={C.NewButton}
                        label={titleLong}
                        icon="jp-icon-add"
                        color="primary"
                        variant="dashed"
                        size="lg"
                        action={action}
                    />
                </div>
            </ListBlock>
        </>
    );
}

export interface ListResultProps {
    items: ReactElement[];
    isSearching: boolean;
    emptyItem: ReactNode;
    query: QStatus;
}

export function ListResult({ items, isSearching, query, emptyItem }: ListResultProps) {
    const { isPending, error } = query;
    // // If we are still loading or have errors, we can not know if we are empty
    const isIncomplete = isPending || !!error;
    const content = items.length ? items : isSearching ? <NoResults /> : isIncomplete ? null : emptyItem;
    return (
        <>
            <QueryErrorBlocks query={query} />
            {content}
            {isPending && <SkeletonBlock />}
        </>
    );
}
